import React, {
  ChangeEvent,
  KeyboardEvent,
  memo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import styled from "@emotion/styled";
import { Box, Button, Dialog, TextField, Typography } from "@mui/material";
import { SubmitButton } from "src/components/Button/SubmitButton";
import { CloseIcon, WorkingTimeSidebarInfoIcon } from "src/Icons";
import { BookingsCodeIcon } from "src/Icons/BookingsCodeIcon";
import { verifyUserPhone } from "src/store/reducers/usersSlice";

const CodeField = styled(TextField)({
  height: "40px",
  width: "40px",
  "& .MuiOutlinedInput-root": {
    height: "40px",
    width: "40px",
    fontWeight: 600,
    fontSize: "24px",
    color: "#1F1F1F",
  },
  "& .MuiInputBase-input.MuiOutlinedInput-input": {
    padding: "0 0 0 0",
    height: "40px",
  },
  "& .MuiInputBase-root.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
    {
      border: "1px solid #676767",
    },
  "& .Mui-focused": {
    borderColor: "#676767",
  },
});

interface PinCodeInputProp {
  value: any;
  onChange: (index: number, value: string) => void;
}

function PinCodeInput ({ value, onChange }: PinCodeInputProp) {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([
    null,
    null,
    null,
    null,
    null,
    null,
  ]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && index > 0 && !value[index]) {
      const previousIndex = index - 1;
      inputRefs.current[previousIndex]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      const nextIndex = index + 1;
      inputRefs.current[nextIndex]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      const previousIndex = index - 1;
      inputRefs.current[previousIndex]?.focus();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        margin: "32px 0 40px 0",
      }}
    >
      {value.map((pinNumber: any, index: any) => (
        <CodeField
          type="tel"
          value={pinNumber}
          id={`pin${index}`}
          key={index}
          inputProps={{
            maxLength: 1,
            style: { textAlign: "center" },
          }}
          sx={{
            margin: " 0 12px 0 0",
            "&:last-of-type": {
              margin: 0,
            },
          }}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange(index, e.target.value)
          }
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
            handleKeyDown(e, index)
          }
          inputRef={(el) => (inputRefs.current[index] = el)}
        />
      ))}
    </Box>
  );
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  callback: () => void;
}

const CodeModal = memo(({
  isOpen,
  onClose,
  onSubmit,
  callback,
}: ModalProps) => {
  const dispatch = useDispatch();
  const [pinCode, setPinCode] = useState(["", "", "", "", "", ""]);

  const handleChange = (index: number, value: string) => {
    const newPinCode = [...pinCode];
    newPinCode[index] = value;
    setPinCode(newPinCode);
  };

  const resetModalData = () => {
    setPinCode(["", "", "", "", "", ""]);
  };

  const onCloseModal = () => {
    resetModalData();
    onClose();
  }

  const sendVerifyCode = () => {
    pinCode.join("").length === 6 &&
    dispatch(
      verifyUserPhone({
        code: pinCode.join(""),
        onSuccess: () => {
          resetModalData();
          onSubmit();
        },
        onFailure: (error: any) => {
          alert(JSON.stringify(error));
        },
      })
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: "16px",
          width: "524px",
          maxWidth: "524px",
        },
      }}
    >
      <Box
        sx={{
          padding: "32px",
          borderRadius: "16px",
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              color: "#B9B9B9",
              marginBottom: "12px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#080808",
                  marginRight: "8px",
                }}
              >
                Enter the code
              </Typography>
              <WorkingTimeSidebarInfoIcon fontSize="inherit" />
            </Box>
            <Box
              onClick={onClose}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
                fontSize: "24px",
                cursor: "pointer",

                "&:hover": {
                  color: "#828282",
                },
              }}
            >
              <CloseIcon fontSize="inherit" />
            </Box>
          </Box>
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "22px",
              }}
            >
              <BookingsCodeIcon
                sx={{
                  height: "120px",
                  width: "120px",
                }}
              />
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#1F1F1F",
              textAlign: "center",
              margin: "24px 0 0 0",
            }}
          >
            A code was sent to the phone number you entered just now. If you don't receive the code, please go back and double-check the accuracy of the entered phone number.
          </Typography>
          <PinCodeInput value={pinCode} onChange={handleChange} />
          <Button
            sx={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#BF295C",
              textDecoration: "underline",
              textAlign: "center",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
            disableRipple
            disableTouchRipple
            disableFocusRipple
            onClick={callback}
          >
            Send code again
          </Button>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "40px",
            }}
          >
            <SubmitButton
              sx={{
                width: "222px",
                color: "#BF295C",
                backgroundColor: "white",
                border: "1px solid #BF295C",
                "&.MuiButton-root:hover": {
                  backgroundColor: "white",
                },
              }}
              onClick={onCloseModal}
              title="Go back"
            />
            <SubmitButton
              sx={{
                width: "222px",
              }}
              onClick={sendVerifyCode}
              title="Confirm and continue"
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
})

export {CodeModal}
